import { useState, useEffect, useMemo } from 'react';
import { TablePagination } from '@mui/material';
import moment from 'moment/moment';
import { getRequest } from '../../../../config';

const FEATURE_LABELS = {
    'filing-assistant-allocation': 'Allocation – Filing Assistant',
    'filing-assistant-autofill': 'Auto-fill – Filing Assistant',
    'filing-assistant-other-modules': 'Other Modules – Filing Assistant',
};

const PROVIDER_META = {
    claude: { label: 'Claude', color: '#4A67FF', bg: '#EEF1FF' },
    gpt: { label: 'GPT', color: '#0BA26F', bg: '#E7F8F1' },
    embedding: { label: 'Embeddings', color: '#F0AE0D', bg: '#FDF5E3' },
};

const formatCost = (v) => (v == null ? '—' : v > 0 && v < 0.01 ? '<$0.01' : `$${Number(v).toFixed(2)}`);
const formatTokens = (v) => (v || 0).toLocaleString();

// Mirrors autoFillStepper's getModuleLabel so the same moduleType key reads the same way here
const getModuleLabel = (moduleType) => {
    if (!moduleType) return '';
    if (moduleType === 'repository') return 'Data Repository';
    if (moduleType.includes('business-audit')) return 'Business Audit';
    if (moduleType === 'warranty-disclosure') return 'Warranty Disclosures';
    if (moduleType.includes('directors-questionnaire')) return "Directors' Questionnaire";
    if (moduleType === 'Investment Readiness') return 'Investment Readiness';
    return moduleType;
};

// "Data Repository +1", "Data Repository +2" — primary module filled plus a count of any extra modules
// bundled into the same run (mirrors the Filing Assistant's multi-module select step).
const formatModulesFilled = (moduleType, extraModules) => {
    if (!moduleType) return '';
    const label = getModuleLabel(moduleType);
    const extraCount = (extraModules || []).filter(m => m && m !== moduleType).length;
    return extraCount > 0 ? `${label} +${extraCount}` : label;
};

// Full module list for the hover tooltip, e.g. "Data Repository, Investment Readiness"
const formatModulesFilledTooltip = (moduleType, extraModules) => {
    if (!moduleType) return '';
    const all = [moduleType, ...(extraModules || [])].filter((m, i, arr) => m && arr.indexOf(m) === i);
    return all.map(getModuleLabel).join(', ');
};

// One phase's provider breakdown (Claude / GPT / Embeddings — calls, tokens, cost, and actual model ids used)
const ProviderBreakdown = ({ phase }) => {
    const providerRows = ['claude', 'gpt', 'embedding']
        .map((key) => ({ key, ...PROVIDER_META[key], ...phase[key] }))
        .filter((p) => p.calls > 0);
    return (
        <div className="df fdc" style={{ gap: '0.5rem' }}>
            {providerRows.map((p) => (
                <div key={p.key} className="bg1 br10px px1rem py0-75rem bw1px bss bo29">
                    <div className="df aic" style={{ gap: '0.75rem' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                        <span className="fs0-8rem fw600 c15" style={{ flex: '0 0 110px' }}>{p.label}</span>
                        <span className="fs0-75rem fw400 c28" style={{ flex: 1 }}>{p.calls} call{p.calls !== 1 ? 's' : ''}</span>
                        <span className="fs0-8rem fw500 c14" style={{ flex: '0 0 100px', textAlign: 'right' }}>{formatTokens(p.tokens)} tok</span>
                        <span className="fs0-8rem fw600 c6" style={{ flex: '0 0 80px', textAlign: 'right' }}>{formatCost(p.cost)}</span>
                    </div>
                    {p.models?.length > 0 && (
                        <div className="df fww" style={{ gap: '0.4rem', marginTop: '0.5rem', marginLeft: '1.15rem' }}>
                            {p.models.map((m) => (
                                <span key={m} className="fs0-7rem fw500 c14" style={{ background: '#F0F2F5', borderRadius: '999px', padding: '0.15rem 0.6rem', lineHeight: 1.4 }}>{m}</span>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

// One phase (Allocation / Auto-fill / Other Modules) within a run — its own files/questions/cost + providers
const PhaseRow = ({ phase }) => (
    <div className="bg1 br10px px1rem py1rem mb0-75rem" style={{ border: '1px solid #E8EBF5' }}>
        <div className="df aic jcsb mb0-75rem" style={{ gap: '1rem' }}>
            <span className="fs0-8rem fw600 c15">{FEATURE_LABELS[phase.feature] || phase.feature}</span>
            <span className="fs0-75rem fw400 c28">{moment(phase.createdAt).format('MMM D, YYYY h:mm A')}</span>
        </div>
        <div className="df aic mb0-75rem" style={{ gap: '1.5rem' }}>
            <span className="fs0-75rem fw400 c28" title={formatModulesFilledTooltip(phase.moduleType, phase.extraModules)}>{formatModulesFilled(phase.moduleType, phase.extraModules) || '—'}</span>
            <span className="fs0-75rem fw400 c28">{phase.filesUploaded || 0} files uploaded</span>
            <span className="fs0-75rem fw400 c28">{phase.questionsProcessed || 0} questions answered</span>
            <span className="fs0-75rem fw400 c28">{phase.totalCalls} AI action{phase.totalCalls !== 1 ? 's' : ''}</span>
            <span className="fs0-75rem fw400 c28">{formatTokens(phase.totalTokens)} tokens</span>
            <span className="fs0-8rem fw600 c6">{formatCost(phase.totalCost)}</span>
        </div>
        <ProviderBreakdown phase={phase} />
    </div>
);

// One run's summary row (files uploaded, questions processed, cost — combined across phases) + an expandable
// list of each phase (Allocation, Auto-fill, Other Modules) that shares this run's fileReportId.
const RunRow = ({ run }) => {
    const [open, setOpen] = useState(false);
    const featureLabel = run.phases.length > 1
        ? run.phases.map(p => FEATURE_LABELS[p.feature] || p.feature).join(' → ')
        : (FEATURE_LABELS[run.phases[0]?.feature] || run.phases[0]?.feature);
    const providerKeys = new Set();
    run.phases.forEach(p => ['claude', 'gpt', 'embedding'].forEach(key => { if (p[key]?.calls > 0) providerKeys.add(key); }));

    return (
        <div className="bg1 br10px bw1px bss bo29 mb1rem oh">
            <div
                onClick={() => setOpen(o => !o)}
                className="df aic px1-5rem py1-5rem cp bg5-hover td250"
                style={{ gap: '1.5rem' }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ transition: 'transform 200ms', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }}>
                    <path d="M9 6l6 6-6 6" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <div className="df fdc" style={{ flex: '1 1 160px', minWidth: 0 }}>
                    <span className="fs0-875rem fw500 c15 oh" style={{ display: 'block', width: '100%', minWidth: 0, textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.userId ? `${run.userId.firstName || ''} ${run.userId.lastName || ''}`.trim() || '—' : '—'}</span>
                    <span className="fs0-75rem fw400 c28 oh" style={{ display: 'block', width: '100%', minWidth: 0, textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.userId?.email || '—'}</span>
                    <span className="fs0-75rem fw400 c28">{moment(run.latestAt).format('MMM D, YYYY h:mm A')}</span>
                </div>

                <div className="df fdc" style={{ flex: '1 1 180px', minWidth: 0 }}>
                    <span className="fs0-875rem fw600 c6 oh" style={{ display: 'block', width: '100%', minWidth: 0, textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.companyId?.cName || '—'}</span>
                </div>

                <div className="df fdc" style={{ flex: '1 1 180px', minWidth: 0 }} title={featureLabel}>
                    <span className="fs0-8rem fw500 c14 oh" style={{ display: 'block', width: '100%', minWidth: 0, textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{featureLabel}</span>
                </div>

                <div className="df fdc" style={{ flex: '1 1 160px', minWidth: 0 }} title={formatModulesFilledTooltip(run.moduleType, run.extraModules)}>
                    <span className="fs0-8rem fw500 c15 oh" style={{ display: 'block', width: '100%', minWidth: 0, textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatModulesFilled(run.moduleType, run.extraModules) || '—'}</span>
                </div>

                <div className="df aic" style={{ flex: '1 1 220px', gap: '0.4rem' }}>
                    {[...providerKeys].map(key => (
                        <span key={key} className="fs0-75rem fw600 br50px px0-75rem py0-25rem" style={{ background: PROVIDER_META[key].bg, color: PROVIDER_META[key].color }}>
                            {PROVIDER_META[key].label}
                        </span>
                    ))}
                </div>

                <div className="df fdc tar" style={{ flex: '0 0 100px' }}>
                    <span className="fs0-875rem fw500 c15">{run.filesUploaded || 0}</span>
                    <span className="fs0-75rem fw400 c28">files</span>
                </div>

                <div className="df fdc tar" style={{ flex: '0 0 110px' }}>
                    <span className="fs0-875rem fw500 c15">{run.questionsProcessed || 0}</span>
                    <span className="fs0-75rem fw400 c28">answered</span>
                </div>

                <div className="df fdc tar" style={{ flex: '0 0 90px' }}>
                    <span className="fs1rem fw600 c40">{formatCost(run.totalCost)}</span>
                </div>
            </div>

            {open && (
                <div className="bg54 px1-5rem py1rem" style={{ borderTop: '1px solid #E8EBF5' }}>
                    {run.phases.map((phase) => <PhaseRow key={phase._id} phase={phase} />)}
                </div>
            )}
        </div>
    );
};

const StatTile = ({ label, value, color }) => (
    <div className="bg38 br10px p1-5rem" style={{ flex: 1 }}>
        <p className="fs1-75rem fw600 mb0-25rem" style={{ color }}>{value}</p>
        <p className="fs0-8rem fw400 c28 mb0rem">{label}</p>
    </div>
);

const AiCosting = () => {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(50);
    const [feature, setFeature] = useState('');
    const [provider, setProvider] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: page + 1, limit });
            if (feature) params.append('feature', feature);
            if (provider) params.append('provider', provider);
            if (search.trim()) params.append('search', search.trim());
            const response = await getRequest(`/admin/ai-usage-logs?${params.toString()}`);
            if (response?.code === 200) {
                setLogs(response.body.logs || []);
                setTotal(response.body.total || 0);
            }
        } catch (error) {
            console.error('Error fetching AI usage logs:', error);
        } finally {
            setLoading(false);
        }
    };

    // Debounce the free-text search so we don't fire a request on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => { setPage(0); fetchLogs(); }, 350);
        return () => clearTimeout(timer);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchLogs(); }, [page, limit, feature, provider]); // eslint-disable-line react-hooks/exhaustive-deps

    const pageStats = useMemo(() => ({
        cost: logs.reduce((sum, r) => sum + (r.totalCost || 0), 0),
        files: logs.reduce((sum, r) => sum + (r.filesUploaded || 0), 0),
        questions: logs.reduce((sum, r) => sum + (r.questionsProcessed || 0), 0),
    }), [logs]);

    return (
        <div>
            <div className="df aic mb1-5rem" style={{ gap: '1rem' }}>
                <StatTile label="Total runs (this page)" value={total} color="#151313" />
                <StatTile label="Files uploaded (this page)" value={pageStats.files} color="#4A67FF" />
                <StatTile label="Questions Answered (this page)" value={pageStats.questions} color="#0BA26F" />
                <StatTile label="Total cost (this page)" value={formatCost(pageStats.cost)} color="#40C353" />
            </div>

            <div className="df aic jcsb mb1-5rem" style={{ gap: '0.75rem' }}>
                <div className="df aic" style={{ gap: '0.75rem' }}>
                    <select
                        value={feature}
                        onChange={(e) => { setPage(0); setFeature(e.target.value); }}
                        className="br50px px1-5rem py0-5rem bw1px bss bo29 bg1 fs0-875rem fw500 c15 cp"
                        style={{ minWidth: 260, appearance: 'none', outline: 'none' }}
                    >
                        <option value="">All Features</option>
                        {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>

                    <select
                        value={provider}
                        onChange={(e) => { setPage(0); setProvider(e.target.value); }}
                        className="br50px px1-5rem py0-5rem bw1px bss bo29 bg1 fs0-875rem fw500 c15 cp"
                        style={{ minWidth: 180, appearance: 'none', outline: 'none' }}
                    >
                        <option value="">All Providers</option>
                        {Object.entries(PROVIDER_META).map(([key, meta]) => (
                            <option key={key} value={key}>{meta.label}</option>
                        ))}
                    </select>
                </div>

                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by company or user…"
                    className="br50px px1-5rem py0-5rem bw1px bss bo29 bg1 fs0-875rem fw400 c15"
                    style={{ minWidth: 260, outline: 'none' }}
                />
            </div>

            {/* Column headers */}
            <div className="df aic px1-5rem py0-5rem mb0-5rem" style={{ gap: '1.5rem' }}>
                <div style={{ width: 14, flexShrink: 0 }} />
                <span className="fs0-7rem fw600 c28 ttu" style={{ flex: '1 1 160px', letterSpacing: '0.05em' }}>Run By</span>
                <span className="fs0-7rem fw600 c28 ttu" style={{ flex: '1 1 180px', letterSpacing: '0.05em' }}>Company</span>
                <span className="fs0-7rem fw600 c28 ttu" style={{ flex: '1 1 180px', letterSpacing: '0.05em' }}>Feature</span>
                <span className="fs0-7rem fw600 c28 ttu" style={{ flex: '1 1 160px', letterSpacing: '0.05em' }}>Modules Filled</span>
                <span className="fs0-7rem fw600 c28 ttu" style={{ flex: '1 1 220px', letterSpacing: '0.05em' }}>Providers Used</span>
                <span className="fs0-7rem fw600 c28 ttu tar" style={{ flex: '0 0 100px', letterSpacing: '0.05em' }}>Files Uploaded</span>
                <span className="fs0-7rem fw600 c28 ttu tar" style={{ flex: '0 0 110px', letterSpacing: '0.05em' }}>Questions Answered</span>
                <span className="fs0-7rem fw600 c28 ttu tar" style={{ flex: '0 0 90px', letterSpacing: '0.05em' }}>Cost</span>
            </div>

            {loading ? (
                <div className="df aic jcc bg38 br10px" style={{ minHeight: '30vh' }}>
                    <p className="fs1rem fw500 c28">Loading…</p>
                </div>
            ) : logs.length ? (
                <>
                    {logs.map((run) => <RunRow key={run._id} run={run} />)}
                    <div className="df jcfe mt0-5rem">
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={limit}
                            onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </div>
                </>
            ) : (
                <div className="df aic jcc fdc bg38 br10px" style={{ minHeight: '30vh' }}>
                    <p className="fs1rem fw500 c15 mb0-25rem">No AI usage recorded yet</p>
                    <p className="fs0-8rem fw400 c28">Costs will appear here after a Filing Assistant run completes.</p>
                </div>
            )}
        </div>
    );
};

export default AiCosting;
